import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  type OptionSetAttribute,
  optionSetQueryOptions,
} from '@/services/animal-management/api';
import { useAccessToken } from '@/services/auth';

type OptionSetSelectProps = {
  id: string;
  objectTypeCode: number;
  attribute: OptionSetAttribute;
  value: string | null | undefined;
  onChange: (value: string) => void;
  invalid?: boolean;
  disabled?: boolean;
};

export function OptionSetSelect({
  id,
  objectTypeCode,
  attribute,
  value,
  onChange,
  invalid,
  disabled,
}: OptionSetSelectProps) {
  const { t } = useTranslation();
  const token = useAccessToken();
  const { data = [], isLoading } = useQuery(
    optionSetQueryOptions(token, objectTypeCode, attribute),
  );

  // Keep a stored value selectable even if it is no longer in the option set.
  const options = useMemo(
    () => (value && !data.includes(value) ? [value, ...data] : data),
    [data, value],
  );

  return (
    <Select
      value={value ?? ''}
      onChange={(next) => onChange((next as string | null) ?? '')}
      disabled={disabled || isLoading}
    >
      <SelectTrigger id={id} aria-invalid={invalid} className="w-full">
        <SelectValue placeholder={t('common.selectOption', 'Select Option')} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option} value={option}>
            {option}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
